/* ============================================================
   Master Collection template — shared JS.

   One dedicated asset file (loaded via `type="module"`, so the browser
   fetches/executes it once no matter how many sections on this template
   reference it) rather than adding functions to assets/custom.js, mirroring
   how this template's CSS already lives in its own
   assets/template-master-collection.css instead of custom.css.
   ============================================================ */

/* ============================================================
   Jump-to-size nav — sections/master-collection-rows.liquid

   Delegated per .mc-rows section rather than one global listener: none of
   this theme's existing jump-to-section patterns (bd-shop-by-event, matched
   by handleized name; bd-cta-grid, matched by data-section-id) fit here,
   since data-section-id can't tell rows apart when they all come from the
   repeatable sections/master-collection-product-row.liquid — so this is
   matched by subcollection/collection handle instead
   (data-mc-jump-target <-> data-mc-row). If a pill's collection has no
   matching product-row section added to the page, the click is a silent
   no-op by design ("scroll to it if present").

   Idempotent: guarded by a flag on each .mc-rows section, so theme-editor
   reloads (shopify:section:load) never double-bind.
   ============================================================ */
function bdMasterCollectionJumpNavInit(root) {
  const scope = root || document;

  scope.querySelectorAll('.mc-rows').forEach((section) => {
    if (section.dataset.mcJumpBound === 'true') return;
    section.dataset.mcJumpBound = 'true';

    // Same read as bd-cta-grid's / bd-faq-toggle's jump-scroll handlers in
    // this theme: --bd-header-stuck-height reflects what the sticky header
    // stack actually paints right now, not the flat --header-height constant.
    const headerHeight = () => {
      const rootStyle = getComputedStyle(document.documentElement);
      return (
        parseFloat(rootStyle.getPropertyValue('--bd-header-stuck-height')) ||
        parseFloat(rootStyle.getPropertyValue('--header-height')) ||
        0
      );
    };

    section.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-mc-jump-target]');
      if (!pill || !section.contains(pill)) return;

      const target = document.querySelector(
        '[data-mc-row="' + CSS.escape(pill.dataset.mcJumpTarget) + '"]'
      );
      if (!target) return;

      target.style.scrollMarginTop = `calc(${headerHeight()}px + 24px)`;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bdMasterCollectionJumpNavInit());
document.addEventListener('shopify:section:load', (event) => bdMasterCollectionJumpNavInit(event.target));

/* ============================================================
   Jump-to-size slider arrows — sections/master-collection-rows.liquid

   Same scroll-by-one-pill-width mechanic as bdMasterCollectionRowInit
   below (disabled at each end, whole nav hidden when there is nothing to
   scroll) — kept as its own function under this row's own data-mcj-*
   attributes rather than reusing data-mcr-* directly, per this file's own
   rule of thumb: two unrelated rows should never be silently coupled
   through a shared DOM contract just because the mechanics happen to
   match. The one real difference from data-mcr-*'s lookup: here
   data-mcj-nav is a descendant of data-mcj-carousel (it overlays the
   track's own two ends), not a sibling of it.

   Idempotent: listeners are attached once per track, guarded by a flag on
   the element, so theme-editor reloads never double-bind.
   ============================================================ */
function bdMasterCollectionJumpCarouselInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-mcj-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-mcj-track]');
    const nav = carousel.querySelector('[data-mcj-nav]');
    const prev = carousel.querySelector('[data-mcj-prev]');
    const next = carousel.querySelector('[data-mcj-next]');

    if (!track) return;

    const step = () => {
      const pill = track.querySelector('.mc-jump__pill');
      if (!pill) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      return pill.offsetWidth + gap;
    };

    const sync = () => {
      // 2px of slack absorbs sub-pixel scroll positions.
      const max = track.scrollWidth - track.clientWidth;

      if (nav) nav.hidden = max <= 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };

    if (!track._mcjBound) {
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });

      // Pill widths depend on the container, so re-check on resize.
      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(track);
      } else {
        window.addEventListener('resize', sync);
      }

      track._mcjBound = true;
    }

    sync();
  });
}

document.addEventListener('DOMContentLoaded', () => bdMasterCollectionJumpCarouselInit());
document.addEventListener('shopify:section:load', (event) => bdMasterCollectionJumpCarouselInit(event.target));

/* ============================================================
   Product row carousel arrows — sections/master-collection-product-row.liquid

   Structurally identical to bdMinistryCollectionsInit() in custom.js (the
   track scrolls natively via scroll-snap + overflow-x; this only wires the
   arrow buttons, keeps their disabled state in sync, and hides the whole
   nav when there is nothing to scroll) — kept as its own function under
   this section's own class/data-attribute names rather than literally
   reusing bd-mc's, since that would silently couple two unrelated sections
   through a shared DOM contract.

   Idempotent: listeners are attached once per track, guarded by a flag on
   the element, so theme-editor reloads never double-bind.
   ============================================================ */
function bdMasterCollectionRowInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-mcr-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-mcr-track]');
    const nav = carousel.parentElement?.querySelector('[data-mcr-nav]');
    const prev = carousel.parentElement?.querySelector('[data-mcr-prev]');
    const next = carousel.parentElement?.querySelector('[data-mcr-next]');

    if (!track) return;

    const step = () => {
      const card = track.querySelector('.mc-row__item');
      if (!card) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return card.offsetWidth + gap;
    };

    const sync = () => {
      // 2px of slack absorbs sub-pixel scroll positions.
      const max = track.scrollWidth - track.clientWidth;

      if (nav) nav.hidden = max <= 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };

    if (!track._mcrBound) {
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });

      // Card widths depend on the container, so re-check on resize.
      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(track);
      } else {
        window.addEventListener('resize', sync);
      }

      track._mcrBound = true;
    }

    sync();
  });
}

document.addEventListener('DOMContentLoaded', () => bdMasterCollectionRowInit());
document.addEventListener('shopify:section:load', (event) => bdMasterCollectionRowInit(event.target));
